import React, { useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useMatch,
  useParams,
} from "react-router-dom";
import { Provider } from "react-redux";
import { LDProvider } from "launchdarkly-react-client-sdk";
import store from "../../store";
import { ThemeProvider } from "@emotion/react";
import theme from "../../themes/theme";
import AuthorizationProvider from "../../common/providers/AuthorizationProvider";
import LaunchDarklyProvider from "../../common/launchDarkly/LaunchDarklyProvider";
import ProjectsMspa from "../../pages/Projects/ProjectsMspa";
import { GovernanceV2Mspa } from "../../pages/GovernanceV2";
import { TravelAndExpenseV2Mspa } from "../../pages/TravelAndExpenseV2";
import { VendorQuotesV2Mspa } from "../../pages/VendorQuotesV2";
import { ProjectVersionsV2Mspa } from "../../pages/ProjectVersionsV2";
import { Show as ServicesV2Show } from "../../pages/ProjectServicesV2/Show";
import CustomerSummaryV2 from "../../pages/CustomerSummaryV2/CustomerSummaryV2";
import CollaboratorsV2 from "../../pages/CollaboratorsV2/CollaboratorsV2";
import ResourcesV2Mspa from "../../pages/ResourcesV2/ResourcesV2Mspa";
import ProjectSurveysMspa from "../../pages/ProjectSurveysV2/ProjectSurveysMspa";
import SurveyMspa from "../../pages/Surveys/SurveyMspa";
import PendingApprovals from "../../pages/PendingApprovals";
import DocumentDeliveries from "../../pages/DocumentDeliveries";
import AccountHealthSnapshotsMspa from "../../pages/AccountHealthSnapshots";
import { Dashboard } from "../../pages/DashboardV2";
import Products from "../../pages/Products";
import ProjectLocationsV2Mspa from "../../pages/ProjectLocationsV2";
import ProjectContactsV2Mspa from "../../pages/ProjectContactsV2";
import CustomerSuccessesMspa from "../../pages/CustomerSuccesses/CustomerSuccessesMspa";
import NotesV2Mspa from "../../pages/NotesV2";
import WorkBreakdownV2Mspa from "../../pages/WorkBreakdownV2";
import ProjectApprovalsV2Mspa from "../../pages/ProjectApprovalsV2";
import PaymentCreditsV2Mspa from "../../pages/Pricing/PaymentCreditsV2";
import ThirdPartyPricingV2Mspa from "../../pages/Pricing/ThirdPartyPricingV2";
import ProductPricingV2Mspa from "../../pages/Pricing/ProductPricingV2";
import ProjectPricingV2Mspa from "../../pages/Pricing/ProjectPricingV2";
import MsPricingV2Mspa from "../../pages/Pricing/MsPricingV2";
import PsPricingV2Mspa from "../../pages/Pricing/PsPricingV2";
import ResourcePlanningMspa from "../../pages/ResourcePlanning/ResourcePlanningMspa";
import ProjectConditions from "../../pages/ConditionsMspa/ProjectConditions";
import { ProjectAttachmentsMspa } from "../../pages/Projects/ProjectAttachments";
import PsaProjectV2Mspa from "../../pages/PsaProject";
import AccountMspa from "../../pages/Account/AccountMspa";
import ProjectNavigationProvider from "./ProjectNavigationProvider";
import Sidebar, {
  SIDEBAR_EXPANDED_WIDTH,
  SIDEBAR_COLLAPSED_WIDTH,
} from "./Sidebar";

interface ProjectLayoutProps {
  account_slug: string;
  account_id: string;
}

const ProjectWithNavWrapper: React.FC<{
  account_slug: string;
  sidebarWidth: number;
  children: React.ReactElement | ((projectId: string) => React.ReactElement);
}> = ({ account_slug, sidebarWidth, children }) => {
  const { id } = useParams<{ id: string }>();

  return (
    <ProjectNavigationProvider
      account_slug={account_slug}
      sidebarWidth={sidebarWidth}
      projectId={id!}
    >
      {typeof children === "function" ? children(id!) : children}
    </ProjectNavigationProvider>
  );
};

const ProjectsWithNav: React.FC<{
  account_slug: string;
  account_id: string;
  sidebarWidth: number;
}> = ({ account_slug, account_id, sidebarWidth }) => {
  const projectMatch = useMatch("/projects/:id/*");
  const projectId = projectMatch?.params?.id;

  const mspa = (
    <ProjectsMspa
      account_slug={account_slug}
      account_id={account_id}
      save_to_cloud_storage_flag={undefined}
    />
  );

  if (projectId) {
    return (
      <ProjectNavigationProvider
        account_slug={account_slug}
        sidebarWidth={sidebarWidth}
        projectId={projectId}
      >
        {mspa}
      </ProjectNavigationProvider>
    );
  }

  return mspa;
};

export const ProjectLayoutInner: React.FC<ProjectLayoutProps> = ({
  account_slug,
  account_id,
}) => {
  const [sidebarExpanded, setSidebarExpanded] = useState(true);

  const sidebarWidth = sidebarExpanded
    ? SIDEBAR_EXPANDED_WIDTH
    : SIDEBAR_COLLAPSED_WIDTH;

  const props = {
    account_slug,
    account_id: undefined,
    save_to_cloud_storage_flag: undefined,
  };

  return (
    <>
      <Sidebar
        expanded={sidebarExpanded}
        onToggle={() => setSidebarExpanded((prev) => !prev)}
        clientSideNav
      />
      <div
        style={{
          marginLeft: sidebarWidth,
          height: "100vh",
          overflowY: "auto",
          backgroundColor: "#F8F9FA",
          transition: "margin-left 0.2s ease",
        }}
      >
        <div id="project-content">
          <Routes>
            <Route path="/users/*" element={<AccountMspa />} />
            <Route
              path="/projects/:id/project_governances/*"
              element={
                <ProjectWithNavWrapper
                  account_slug={account_slug}
                  sidebarWidth={sidebarWidth}
                >
                  {(projectId) => <GovernanceV2Mspa project_id={projectId} />}
                </ProjectWithNavWrapper>
              }
            />
            <Route
              path="/projects/:id/project_expenses/*"
              element={
                <ProjectWithNavWrapper
                  account_slug={account_slug}
                  sidebarWidth={sidebarWidth}
                >
                  {(projectId) => (
                    <TravelAndExpenseV2Mspa project_id={projectId} />
                  )}
                </ProjectWithNavWrapper>
              }
            />
            <Route
              path="/projects/:id/project_editor/*"
              element={
                <ProjectWithNavWrapper
                  account_slug={account_slug}
                  sidebarWidth={sidebarWidth}
                >
                  <ServicesV2Show
                    service_type="professional_services"
                    account_slug={account_slug}
                  />
                </ProjectWithNavWrapper>
              }
            />
            <Route
              path="/projects/:id/managed_service/*"
              element={
                <ProjectWithNavWrapper
                  account_slug={account_slug}
                  sidebarWidth={sidebarWidth}
                >
                  <ServicesV2Show
                    service_type="managed_services"
                    account_slug={account_slug}
                  />
                </ProjectWithNavWrapper>
              }
            />
            <Route
              path="/projects/:id/sow_outcome/*"
              element={
                <ProjectWithNavWrapper
                  account_slug={account_slug}
                  sidebarWidth={sidebarWidth}
                >
                  {(projectId) => <CustomerSummaryV2 project_id={projectId} />}
                </ProjectWithNavWrapper>
              }
            />
            <Route
              path="/projects/:id/project_locations/*"
              element={
                <ProjectWithNavWrapper
                  account_slug={account_slug}
                  sidebarWidth={sidebarWidth}
                >
                  {(projectId) => (
                    <ProjectLocationsV2Mspa project_id={projectId} />
                  )}
                </ProjectWithNavWrapper>
              }
            />
            <Route
              path="/projects/:id/project_contacts/*"
              element={
                <ProjectWithNavWrapper
                  account_slug={account_slug}
                  sidebarWidth={sidebarWidth}
                >
                  {(projectId) => (
                    <ProjectContactsV2Mspa project_id={projectId} />
                  )}
                </ProjectWithNavWrapper>
              }
            />
            <Route
              path="/projects/:id/project_conditions/*"
              element={
                <ProjectWithNavWrapper
                  account_slug={account_slug}
                  sidebarWidth={sidebarWidth}
                >
                  {(projectId) => (
                    <ProjectConditions
                      account_slug={account_slug}
                      project_id={projectId}
                      account_id={account_id}
                    />
                  )}
                </ProjectWithNavWrapper>
              }
            />
            <Route
              path="/projects/:id/project_collaborators/*"
              element={
                <ProjectWithNavWrapper
                  account_slug={account_slug}
                  sidebarWidth={sidebarWidth}
                >
                  {(projectId) => (
                    <CollaboratorsV2
                      project_id={projectId}
                      account_slug={account_slug}
                    />
                  )}
                </ProjectWithNavWrapper>
              }
            />
            <Route
              path="/projects/:id/customer_successes/*"
              element={
                <ProjectWithNavWrapper
                  account_slug={account_slug}
                  sidebarWidth={sidebarWidth}
                >
                  {(projectId) => (
                    <CustomerSuccessesMspa
                      account_slug={account_slug}
                      project_id={projectId}
                      account_id={account_id}
                    />
                  )}
                </ProjectWithNavWrapper>
              }
            />
            <Route
              path="/projects/:id/quotes/*"
              element={
                <ProjectWithNavWrapper
                  account_slug={account_slug}
                  sidebarWidth={sidebarWidth}
                >
                  {(projectId) => <VendorQuotesV2Mspa project_id={projectId} />}
                </ProjectWithNavWrapper>
              }
            />
            <Route
              path="/projects/:id/project_surveys/*"
              element={
                <ProjectWithNavWrapper
                  account_slug={account_slug}
                  sidebarWidth={sidebarWidth}
                >
                  {(projectId) => (
                    <ProjectSurveysMspa
                      account_slug={account_slug}
                      project_id={projectId}
                    />
                  )}
                </ProjectWithNavWrapper>
              }
            />
            <Route
              path="/projects/:id/project_materials/*"
              element={
                <ProjectWithNavWrapper
                  account_slug={account_slug}
                  sidebarWidth={sidebarWidth}
                >
                  <Products account_slug={account_slug} />
                </ProjectWithNavWrapper>
              }
            />
            <Route
              path="/projects/:id/project_resources/*"
              element={
                <ProjectWithNavWrapper
                  account_slug={account_slug}
                  sidebarWidth={sidebarWidth}
                >
                  {(projectId) => (
                    <ResourcesV2Mspa
                      account_slug={account_slug}
                      project_id={projectId}
                    />
                  )}
                </ProjectWithNavWrapper>
              }
            />
            <Route
              path="/projects/:id/summary/*"
              element={
                <ProjectWithNavWrapper
                  account_slug={account_slug}
                  sidebarWidth={sidebarWidth}
                >
                  {(projectId) => (
                    <WorkBreakdownV2Mspa project_id={projectId} />
                  )}
                </ProjectWithNavWrapper>
              }
            />
            <Route
              path="/projects/:id/project_approvals/*"
              element={
                <ProjectWithNavWrapper
                  account_slug={account_slug}
                  sidebarWidth={sidebarWidth}
                >
                  {(projectId) => (
                    <ProjectApprovalsV2Mspa project_id={projectId} />
                  )}
                </ProjectWithNavWrapper>
              }
            />
            <Route
              path="/projects/:id/project_versions/*"
              element={
                <ProjectWithNavWrapper
                  account_slug={account_slug}
                  sidebarWidth={sidebarWidth}
                >
                  {(projectId) => (
                    <ProjectVersionsV2Mspa
                      account_slug={account_slug}
                      project_id={projectId}
                    />
                  )}
                </ProjectWithNavWrapper>
              }
            />
            <Route
              path="/projects/:id/notes/*"
              element={
                <ProjectWithNavWrapper
                  account_slug={account_slug}
                  sidebarWidth={sidebarWidth}
                >
                  {(projectId) => <NotesV2Mspa project_id={projectId} />}
                </ProjectWithNavWrapper>
              }
            />
            <Route
              path="/projects/:id/project_credits/*"
              element={
                <ProjectWithNavWrapper
                  account_slug={account_slug}
                  sidebarWidth={sidebarWidth}
                >
                  {(projectId) => (
                    <PaymentCreditsV2Mspa project_id={projectId} />
                  )}
                </ProjectWithNavWrapper>
              }
            />
            <Route
              path="/projects/:id/third_party_pricing/*"
              element={
                <ProjectWithNavWrapper
                  account_slug={account_slug}
                  sidebarWidth={sidebarWidth}
                >
                  {(projectId) => (
                    <ThirdPartyPricingV2Mspa project_id={projectId} />
                  )}
                </ProjectWithNavWrapper>
              }
            />
            <Route
              path="/projects/:id/project_material_pricings/*"
              element={
                <ProjectWithNavWrapper
                  account_slug={account_slug}
                  sidebarWidth={sidebarWidth}
                >
                  {(projectId) => (
                    <ProductPricingV2Mspa project_id={projectId} />
                  )}
                </ProjectWithNavWrapper>
              }
            />
            <Route
              path="/projects/:id/project_pricing/*"
              element={
                <ProjectWithNavWrapper
                  account_slug={account_slug}
                  sidebarWidth={sidebarWidth}
                >
                  {(projectId) => (
                    <ProjectPricingV2Mspa project_id={projectId} />
                  )}
                </ProjectWithNavWrapper>
              }
            />
            <Route
              path="/projects/:id/service_summary/*"
              element={
                <ProjectWithNavWrapper
                  account_slug={account_slug}
                  sidebarWidth={sidebarWidth}
                >
                  {(projectId) => (
                    <MsPricingV2Mspa
                      project_id={projectId}
                      account_id={account_id}
                    />
                  )}
                </ProjectWithNavWrapper>
              }
            />
            <Route
              path="/projects/:id/sow_pricings/*"
              element={
                <ProjectWithNavWrapper
                  account_slug={account_slug}
                  sidebarWidth={sidebarWidth}
                >
                  {(projectId) => (
                    <PsPricingV2Mspa
                      project_id={projectId}
                      account_id={account_id}
                    />
                  )}
                </ProjectWithNavWrapper>
              }
            />
            <Route
              path="/projects/:id/resource_plans/*"
              element={
                <ProjectWithNavWrapper
                  account_slug={account_slug}
                  sidebarWidth={sidebarWidth}
                >
                  {(projectId) => (
                    <ResourcePlanningMspa
                      account_slug={account_slug}
                      project_id={projectId}
                      account_id={account_id}
                      skipRouter
                    />
                  )}
                </ProjectWithNavWrapper>
              }
            />
            <Route
              path="/projects/:id/attachments/*"
              element={
                <ProjectWithNavWrapper
                  account_slug={account_slug}
                  sidebarWidth={sidebarWidth}
                >
                  {(projectId) => (
                    <ProjectAttachmentsMspa project_id={projectId} />
                  )}
                </ProjectWithNavWrapper>
              }
            />
            <Route
              path="/projects/:id/psa_project/*"
              element={
                <ProjectWithNavWrapper
                  account_slug={account_slug}
                  sidebarWidth={sidebarWidth}
                >
                  {(projectId) => (
                    <PsaProjectV2Mspa
                      project_id={projectId}
                      account_slug={account_slug}
                    />
                  )}
                </ProjectWithNavWrapper>
              }
            />
            <Route path="/home" element={<Dashboard />} />
            <Route path="/surveys/*" element={<SurveyMspa />} />
            <Route path="/pending_approvals" element={<PendingApprovals />} />
            <Route
              path="/document_deliveries"
              element={<DocumentDeliveries />}
            />
            <Route
              path="/account_health_snapshots/*"
              element={<AccountHealthSnapshotsMspa />}
            />
            <Route
              path="/projects/*"
              element={
                <ProjectsWithNav
                  account_slug={account_slug}
                  account_id={account_id}
                  sidebarWidth={sidebarWidth}
                />
              }
            />
          </Routes>
        </div>
      </div>
    </>
  );
};

const ProjectLayout: React.FC<ProjectLayoutProps> = (props) => {
  const { account_slug } = props;

  return (
    <Provider store={store}>
      <AuthorizationProvider account_slug={account_slug}>
        <ThemeProvider theme={theme}>
          <LDProvider
            clientSideID={
              process.env.REACT_APP_LAUNCHDARKLY_CLIENT_ID || "dummyclient"
            }
          >
            <LaunchDarklyProvider accountSlug={account_slug}>
              <BrowserRouter>
                <ProjectLayoutInner {...props} />
              </BrowserRouter>
            </LaunchDarklyProvider>
          </LDProvider>
        </ThemeProvider>
      </AuthorizationProvider>
    </Provider>
  );
};

export default ProjectLayout;
